import { oc } from "@orpc/contract";
import { z } from "zod";

export const McpToolInfoSchema = z.object({
  /** Dotted contract path, e.g. "workflows.trigger" (the identity used in settings). */
  path: z.string(),
  /** The name MCP clients see (underscore form of the path). */
  name: z.string(),
  description: z.string(),
  /** Mutating tool; only offered on connections granted `mcp:write`. */
  write: z.boolean(),
  /** Write that cancels, deletes or overwrites rather than adds. */
  destructive: z.boolean(),
});

export type McpToolInfo = z.infer<typeof McpToolInfoSchema>;

export const McpToolGroupSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Reads first, then writes; stable order. */
  tools: z.array(McpToolInfoSchema),
});

export type McpToolGroup = z.infer<typeof McpToolGroupSchema>;

/** Tool decisions as the settings page needs them; both computed server-side. */
export const McpToolStateSchema = z.object({
  /** Contract paths of tools the server actually serves right now. */
  enabledTools: z.array(z.string()),
  /** Catalog paths the org has neither approved nor revoked. */
  needsReview: z.array(z.string()),
});

export type McpToolState = z.infer<typeof McpToolStateSchema>;

/** Result of changing either auto-enable setting. */
export const McpAutoEnableStateSchema = McpToolStateSchema.extend({
  autoEnableNewReadTools: z.boolean(),
  autoEnableNewWriteTools: z.boolean(),
});

export const McpServerSettingsSchema = McpToolStateSchema.extend({
  /** The full MCP endpoint clients are told to connect to. */
  url: z.string(),
  /** Master switch; off until an admin enables it and approves tools. */
  enabled: z.boolean(),
  /** Serve never-decided read tools. */
  autoEnableNewReadTools: z.boolean(),
  /**
   * Also serve never-decided non-destructive write tools. Only ever true while
   * autoEnableNewReadTools is true; destructive tools are never auto-served.
   */
  autoEnableNewWriteTools: z.boolean(),
  groups: z.array(McpToolGroupSchema),
  enabledAt: z.string().nullable(),
  enabledByName: z.string().nullable(),
});

export type McpServerSettings = z.infer<typeof McpServerSettingsSchema>;

export const McpDenialReasonSchema = z.enum([
  "tool_disabled",
  "tool_not_approved",
  "server_disabled",
]);

export type McpDenialReason = z.infer<typeof McpDenialReasonSchema>;

export const McpSettingsEventTypeSchema = z.enum([
  "server_enabled",
  "server_disabled",
  "tools_approved",
  "tools_revoked",
  "auto_enable_changed",
]);

export const McpSettingsEventSchema = z.object({
  id: z.uuid(),
  eventType: McpSettingsEventTypeSchema,
  /** Contract paths the event covers. */
  tools: z.array(z.string()),
  /**
   * Event extras. auto_enable_changed: { setting: "read_tools" | "write_tools",
   * value: boolean }; older events carry only { value } and are read_tools.
   */
  detail: z.unknown().nullable(),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  createdAt: z.string(),
});

export type McpSettingsEvent = z.infer<typeof McpSettingsEventSchema>;

export const McpDenialSchema = z.object({
  id: z.uuid(),
  /** The MCP (underscore) tool name the agent called. */
  toolName: z.string(),
  /** The matching dotted contract path. */
  toolPath: z.string(),
  reason: McpDenialReasonSchema,
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  clientName: z.string().nullable(),
  createdAt: z.string(),
});

export type McpDenial = z.infer<typeof McpDenialSchema>;

/**
 * Admin settings for the Gently MCP server (docs/mcp). Every procedure needs
 * Update:McpServer.
 */
export const mcpServerContracts = {
  get: oc.output(McpServerSettingsSchema).route({
    method: "GET",
    summary: "Get MCP server settings",
    description:
      "Returns the org's MCP server settings for external AI clients: endpoint URL, on/off switch, auto-enable flags, enabled tools, tools needing review and the grouped tool catalog.",
  }),
  /**
   * Approve (enabled: true) or revoke (enabled: false) tools. A delta, not a
   * replacement, so rapid toggles from several clients commute. Approving also
   * clears a revocation and vice versa. Audited in the activity log.
   */
  setToolsEnabled: oc
    .input(
      z.object({
        tools: z.array(z.string()).min(1).max(100),
        enabled: z.boolean(),
      }),
    )
    .output(McpToolStateSchema)
    .route({
      method: "POST",
      summary: "Approve or revoke MCP tools",
      description:
        "Approves or revokes a set of tools for external AI clients on the org's MCP server, without touching other tools; recorded in the activity log.",
    }),
  /**
   * The master switch. Enabling needs a non-empty `approveTools` (the tools
   * the admin ticked); the event records who enabled and exactly those tools.
   * Disabling keeps every tool decision. Both are audited.
   */
  setServerEnabled: oc
    .input(
      z
        .object({
          enabled: z.boolean(),
          approveTools: z.array(z.string()).max(200).optional(),
        })
        .refine((value) => !value.enabled || !!value.approveTools?.length, {
          message: "Approve at least one tool to enable the MCP server.",
          path: ["approveTools"],
        }),
    )
    .output(McpToolStateSchema.extend({ enabled: z.boolean() }))
    .route({
      method: "POST",
      summary: "Turn the MCP server on or off",
      description:
        "Enables the org's MCP server for external AI clients, approving the chosen tools, or disables it while keeping tool decisions; recorded in the activity log.",
    }),
  /**
   * Serve read tools nobody has decided on yet. Turning it off also turns off
   * auto-enabling write tools (both audited).
   */
  setAutoEnableNewReadTools: oc
    .input(z.object({ value: z.boolean() }))
    .output(McpAutoEnableStateSchema)
    .route({
      method: "POST",
      summary: "Auto-enable new MCP read tools",
      description:
        "Turns on or off serving read-only tools nobody has decided on yet; turning it off also stops auto-enabling write tools. Recorded in the activity log.",
    }),
  /**
   * Serve non-destructive write tools nobody has decided on yet (destructive
   * ones never). BAD_REQUEST while auto-enabling read tools is off.
   */
  setAutoEnableNewWriteTools: oc
    .input(z.object({ value: z.boolean() }))
    .output(McpAutoEnableStateSchema)
    .route({
      method: "POST",
      summary: "Auto-enable new MCP write tools",
      description:
        "Turns on or off serving undecided non-destructive write tools to external AI clients; requires auto-enabling read tools to be on. Recorded in the activity log.",
    }),
  /** The admin activity log (enable/disable, approvals, revocations), newest first. */
  listEvents: oc
    .input(
      z.object({
        limit: z.number().int().min(1).max(200).default(50),
        offset: z.number().int().min(0).default(0),
      }),
    )
    .output(
      z.object({
        items: z.array(McpSettingsEventSchema),
        total: z.number().int().nonnegative(),
      }),
    )
    .route({
      method: "GET",
      summary: "List MCP server settings activity",
      description:
        "Pages through the MCP server admin activity log, newest first: server enabled or disabled, tools approved or revoked, auto-enable changes, with who made them.",
    }),
  /** Calls refused (server off, tool revoked or not yet approved), newest first. */
  listDenials: oc
    .input(
      z.object({
        limit: z.number().int().min(1).max(200).default(50),
        offset: z.number().int().min(0).default(0),
      }),
    )
    .output(
      z.object({
        items: z.array(McpDenialSchema),
        total: z.number().int().nonnegative(),
      }),
    )
    .route({
      method: "GET",
      summary: "List refused MCP tool calls",
      description:
        "Pages through tool calls from external AI clients that were refused because the server was off or the tool was revoked or unapproved, newest first, with user and client.",
    }),
};
